import {
  Endpoint,
  EndpointAttributes,
  FilterType,
} from '@context/openapi/types';
import queryString from 'query-string';
import { ARRAY_ITEM_NAME } from '@/constant/Constants';
import { config } from '@/config';
import { captureApiError } from '@/lib/sentry';

// Helper function to check if filter operator expects array values
const isArrayFilterOperator = (operator: string | undefined): boolean => {
  if (!operator) return false;
  return ['in', 'not_in', 'between'].includes(operator.toLowerCase());
};

// Helper to check if filter type requires numeric values
const isNumericFilterType = (filterType: FilterType | undefined): boolean => {
  return filterType === 'TimestampFilter' || filterType === 'NumberFilter';
};

// Helper function to convert comma-separated string to array
// Comma is treated as the delimiter between multiple values
// Examples:
//   "active,cancelled" → ["active", "cancelled"]
//   "active, cancelled" → ["active", "cancelled"] (whitespace trimmed)
//   '["active","cancelled"]' → ["active", "cancelled"] (already JSON array)
//   "active" → ["active"] (single value)
//   "1706000000,1706100000" (timestamp) → [1706000000, 1706100000] (numbers)
const convertToArrayValue = (
  value: string | number,
  convertToNumber: boolean = false
): (string | number)[] => {
  if (typeof value === 'number') return [value];
  if (!value) return [];

  const strValue = String(value);

  // If already in JSON array format like '["a","b"]', parse it
  if (strValue.startsWith('[') && strValue.endsWith(']')) {
    try {
      const parsed = JSON.parse(strValue);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Fallback to comma parsing
    }
  }

  // Parse comma-separated values and return as array
  // Comma is the delimiter, whitespace is trimmed from each value
  const values = strValue
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  // Convert to numbers if required (for timestamps/number filters)
  if (convertToNumber) {
    return values.map((v) => {
      const num = Number(v);
      return isNaN(num) ? v : num;
    });
  }

  return values;
};

const buildUrl = (
  url: string,
  siteId: string,
  endpointAttributes?: EndpointAttributes,
  subDomain?: string
) => {
  const domain = config.cbDomain;
  const protocol = config.cbProtocol;
  if (siteId.indexOf('test') === -1) {
    throw new Error('Invalid siteId');
  }
  let apiUrl = subDomain
    ? `${protocol}://${siteId}.${subDomain}.${domain}/api/v2${url}`
    : `${protocol}://${siteId}.${domain}/api/v2${url}`;
  const queryParams: {
    [key: string]: string | number | (string | number)[];
  } = {};
  if (endpointAttributes && endpointAttributes.pathParameters.length > 0) {
    endpointAttributes.pathParameters.forEach((attribute) => {
      apiUrl = apiUrl.replace(`{${attribute.name}}`, attribute.value as string);
    });
  }
  if (endpointAttributes && endpointAttributes.queryParams.length > 0) {
    endpointAttributes.queryParams
      .filter((a) => a.value != null && a.value != '')
      .forEach((attribute) => {
        if (attribute.filter) {
          const filterKey = `${attribute.name}[${attribute.filter}]`;

          // Check if this is an array filter operator
          if (
            isArrayFilterOperator(attribute.filter) ||
            attribute.filterValueType === 'array'
          ) {
            // Convert comma-separated string to actual array
            // For timestamp/number filters, convert values to numbers
            const shouldConvertToNumber = isNumericFilterType(
              attribute.filterType
            );
            const arrayValue = convertToArrayValue(
              attribute.value!,
              shouldConvertToNumber
            );

            // Serialize as JSON array string: ["active","non_renewing"]
            // The Chargebee API expects this exact format
            queryParams[filterKey] = JSON.stringify(arrayValue);
          } else {
            // Single value filter - pass as-is
            queryParams[filterKey] = attribute.value!;
          }
        } else {
          queryParams[attribute.name] = attribute.value!;
        }
      });
  }
  if (endpointAttributes?.params && endpointAttributes.params.length > 0) {
    endpointAttributes.params
      .filter((a) => a.value != null && a.value != '')
      .forEach((attribute) => {
        if (attribute.filter) {
          const filterKey = `${attribute.name}[${attribute.filter}]`;

          // Check if this is an array filter operator
          if (
            isArrayFilterOperator(attribute.filter) ||
            attribute.filterValueType === 'array'
          ) {
            // Convert comma-separated string to actual array
            // For timestamp/number filters, convert values to numbers
            const shouldConvertToNumber = isNumericFilterType(
              attribute.filterType
            );
            const arrayValue = convertToArrayValue(
              attribute.value!,
              shouldConvertToNumber
            );
            // Serialize as JSON array string
            queryParams[filterKey] = JSON.stringify(arrayValue);
          } else {
            // Single value filter - pass as-is
            queryParams[filterKey] = attribute.value!;
          }
        } else {
          queryParams[attribute.name] = attribute.value!;
        }
      });
    endpointAttributes.params.forEach((attribute) => {
      attribute.child.forEach((child) => {
        if (child.value != null && child.value != '') {
          if (child.arrayIndex != null) {
            // This is to handle array of strings in query params
            // Both array of strings and array of objects are using the same type
            if (child.name === ARRAY_ITEM_NAME) {
              queryParams[`${attribute.name}[${child.arrayIndex}]`] =
                child.value;
            } else {
              queryParams[
                `${attribute.name}[${child.name}][${child.arrayIndex}]`
              ] = child.value;
            }
          } else queryParams[`${attribute.name}[${child.name}]`] = child.value;
        }
      });
    });
  }
  if (Object.keys(queryParams).length > 0) {
    apiUrl +=
      '?' +
      queryString.stringify(queryParams, {
        skipEmptyString: true,
        sort: false,
      });
  }
  return apiUrl;
};

export const fetcher = (
  path: string,
  {
    arg,
  }: {
    arg: {
      siteId: string;
      apiKey: string;
      endpoint?: Endpoint;
      endpointAttributes?: EndpointAttributes;
      userEmail?: string;
    };
  }
) => {
  if (!arg.endpoint) {
    throw new Error('Invalid endpoint');
  }
  const fullUrl = buildUrl(
    arg.endpoint.url,
    arg.siteId,
    arg.endpointAttributes,
    arg.endpoint.subDomain
  );

  const headers: Record<string, string> = {
    Authorization: 'Basic ' + btoa(arg.apiKey),
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
  };

  // Add the user email header if available
  if (arg.userEmail) {
    headers['chargebee-request-origin-user'] = arg.userEmail;
  }

  const options: RequestInit = {
    method: arg.endpoint.method,
    headers,
  };

  return fetch(fullUrl, options).then(async (res) => {
    // Capture 5xx server errors to Sentry
    if (res.status >= 500) {
      const responseBody = await res.clone().text();
      const error = new Error(`Server error: ${res.status} ${res.statusText}`);
      captureApiError(error, {
        endpoint: fullUrl,
        method: arg.endpoint?.method || 'GET',
        statusCode: res.status,
        responseBody,
      });
    }

    return res.json();
  });
};
